/* eslint-disable react/prop-types */
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { addRouteNamespace } from '@Utils/actions';
import {
	getSubscriptions,
	PropertyCard,
	useCompanyDateTime,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import './risk-card.css';
import { fullAccess, viewerAccess } from '@innovyze/shared-utils';
import { useGlobalization } from '@Translations/useGlobalization';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';
import {
	AssetCount,
	AssetCounts,
	RiskConfigCard,
} from '@innovyze/lib-am-common';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { getRiskLandingRoute } from '@Utils/riskTabOrder';
import {
	changeRiskConfigPublishState,
	clearRiskConfig,
	duplicateRiskConfig,
} from '@Actions/Risk.actions';
import {
	selectRiskConfigsPage,
	selectAssetCounts,
	selectIsAssetCountsReady,
} from '@innovyze/lib-am-common/Selectors';
import { selectAssetStore } from '@Selectors';
import { changeRiskConfigsPage } from '@innovyze/lib-am-common/Actions';
import { RiskCalcStatusResult } from '@Types/risk.types';
import { Link, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import HelpIcon from '@mui/icons-material/Help';
import FilterIcon from '@mui/icons-material/FilterAlt';
import { green, grey } from '@mui/material/colors';
import { ConfigPublishState } from '@Types/riskConfig.types';
import { assetSchemaSelectorByType } from '@innovyze/lib_asset_schema_store';
import { AssetFilterListItem } from '@Types/asset.types';

const FilteredResultCount = styled.div({
	display: 'inline-block',
	cursor: 'help',
	'& svg.MuiSvgIcon-root': {
		verticalAlign: 'bottom',
		marginLeft: '0.2em',
		paddingTop: '6px',
		opacity: 0.8,
	},
});

interface Props {
	riskConfig: RiskConfigCard;
	onDuplicateRisk: (riskConfig: RiskConfigCard) => void;
	onDeleteRisk: (id: string, publishState: ConfigPublishState) => void;
}
const RiskListCard = ({ riskConfig, onDuplicateRisk, onDeleteRisk }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);
	const hasViewerAccess = viewerAccess(subscriptions);

	const riskNormalizeFlag = useIsFeatureEnabled(
		'info-360-asset-risk-normalize',
	);
	const isSaveFilterOn = !!useIsFeatureEnabled('info-360-assets-save-filter');
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();

	const { formatDate } = useCompanyDateTime();
	const lastRun = formatDateTimeWithFormatDate(
		riskConfig.lastRun,
		formatDate,
	);
	const riskPage = selectRiskConfigsPage();
	const isAssetCountsReady = selectIsAssetCountsReady();
	const assetCounts: AssetCounts = selectAssetCounts();
	const { assetFilters } = selectAssetStore();

	const handleViewClick = () => {
		dispatch(clearRiskConfig());
		// Decide the landing tab here, using this card's own data (already
		// loaded with the list) — the risk config store won't have this
		// model's data until after the navigation, so this avoids a
		// results-then-lofSetup flash while it loads. `status` (not `lastRun`,
		// which is just the run *start* time) reflects whether a run actually
		// completed — a failed/cancelled/in-progress run should still land on
		// LoF Setup rather than an empty Results grid. Viewers always land on
		// Results since LoF Setup is an edit-only surface they can't use —
		// uses the same viewerAccess() predicate as RiskManagement.page's
		// correction effect (not !hasFullAccess, which also covers
		// inspection-only access and would make the two layers disagree).
		const hasBeenRun = riskConfig.status === 'risk-calc-complete';
		const landingRoute = isRiskTemplatesEnabled
			? getRiskLandingRoute(hasBeenRun, hasViewerAccess)
			: 'results';
		history.push(
			addRouteNamespace(`${landingRoute}/${riskConfig.configId}`),
		);
	};

	const handleDeleteClick = () => {
		onDeleteRisk(riskConfig.configId, riskConfig.publishState);
	};

	const handlePublishStateChange = (newPublishState: ConfigPublishState) => {
		dispatch(
			changeRiskConfigPublishState({
				newPublishState,
				configId: riskConfig.configId,
				refresh: () => {
					dispatch(changeRiskConfigsPage(riskPage.page));
				},
			}),
		);
	};

	const handleDuplicateClick = () => {
		if (isSaveFilterOn) {
			onDuplicateRisk(riskConfig);
		} else {
			const { configId } = riskConfig;
			dispatch(
				duplicateRiskConfig({
					history,
					configId,
					isRiskTemplatesEnabled,
				}),
			);
		}
	};

	const formatRunBy = (runBy: string) => {
		if (runBy.indexOf('@') !== -1) {
			return runBy.substring(0, runBy.lastIndexOf('@'));
		}
		return runBy;
	};

	const menuItems = () => {
		const items = [];

		if (hasFullAccess) {
			if (riskConfig.publishState === ConfigPublishState.published) {
				items.push({
					onClick: () =>
						handlePublishStateChange(
							ConfigPublishState.unpublished,
						),
					text: t('Unpublish'),
				});
			} else {
				items.push({
					onClick: () =>
						handlePublishStateChange(ConfigPublishState.published),
					text: t('Publish'),
				});
			}

			items.push({
				onClick: handleDuplicateClick,
				text: t('Duplicate'),
			});
			items.push({
				onClick: handleDeleteClick,
				text: t('Delete'),
			});
		}

		return items;
	};

	const getRiskStatus = (status: RiskCalcStatusResult) => {
		let result = '-';
		if (status) {
			// DO NOT TRANSLATE THESE... That is done by the status object base on the english string
			switch (status) {
				case 'risk-calc-complete':
					result = 'Complete';
					break;
				case 'risk-calc-cancelled':
					result = 'Cancelled';
					break;
				case 'risk-calc-failed':
				case 'error':
					result = 'Failed';
					break;
				case 'risk-calc-initiated':
				case 'risk-calc-start':
				case 'risk-calc-in-progress':
				case 'risk-calc-getting-assets':
				case 'risk-calc-getting-risk-config':
				case 'risk-calc-getting-inspections':
				case 'risk-calc-archiving-existing-in-snowflake':
				case 'risk-calc-writing-to-snowflake':
				case 'risk-calc-post-processing-normalize':
					result = 'In Progress';
					break;
			}
		}
		return result;
	};

	const getRiskFormula = (riskConfig: RiskConfigCard) => {
		if (
			riskConfig.cofWeighting != undefined &&
			riskConfig.lofWeighting != undefined &&
			riskConfig.riskMethod != undefined
		) {
			return `${riskConfig.lofWeighting}% ${
				riskConfig.riskMethod == 'added' ? '+' : 'x'
			} ${riskConfig.cofWeighting}%`;
		} else {
			return '-';
		}
	};

	const getFormulaValue = (riskConfig: RiskConfigCard) => {
		const formula = getRiskFormula(riskConfig);
		if (riskNormalizeFlag && formula !== '-' && riskConfig.postProcessing) {
			const process = riskConfig.postProcessing.find(
				process => process.type === 'normalize',
			);
			if (process && process.params.useNormalize) {
				const tooltip = `((${formula})/${
					process.params.maxRiskScore ?? t('HCRS')
				}) * ${process.params.normalizeValue ?? t('NV')}`;
				return (
					<div
						title={tooltip}
						style={{
							cursor: 'default',
						}}>
						{t('Normalized')}
						<HelpIcon
							color="secondary"
							style={{
								verticalAlign: 'top',
								height: '20px',
							}}
						/>
					</div>
				);
			}
		}

		return <>{formula}</>;
	};

	const typeCell = (type?: string) => {
		if (!type) {
			return '-';
		}
		const schema = useSelector(state =>
			assetSchemaSelectorByType(state, type),
		);
		const assetDisplay = schema?.displayName;
		const systemDisplay = schema?.systemType.displayName;
		const displayName = `${systemDisplay} - ${assetDisplay}`;
		return displayName ?? '-';
	};

	const renderTitle = useCallback(
		(configName: string, publishState: ConfigPublishState) => {
			if (
				hasFullAccess ||
				publishState === ConfigPublishState.published
			) {
				return (
					<Link
						underline="hover"
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							cursor: 'pointer',
							width: '100%',
							whiteSpace: 'nowrap',
						}}
						title={configName}
						onClick={() => {
							handleViewClick();
						}}
						data-cy="card-title">
						{configName}
					</Link>
				);
			} else {
				return (
					<div
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							width: '100%',
							whiteSpace: 'nowrap',
						}}>
						{configName}
					</div>
				);
			}
		},
		[],
	);

	const renderHeader = (
		configName: string,
		configPublishState: ConfigPublishState,
	) => {
		const tooltipText =
			configPublishState === ConfigPublishState.published
				? t('Published')
				: t('Unpublished');

		const icon =
			configPublishState === ConfigPublishState.published ? (
				<CheckCircleIcon sx={{ color: green[500] }} />
			) : (
				<RemoveCircleIcon sx={{ color: grey[500] }} />
			);

		const publishState = <Tooltip title={tooltipText}>{icon}</Tooltip>;

		return (
			<div className="header-container">
				{renderTitle(configName, configPublishState)} {publishState}
			</div>
		);
	};

	const resultCount =
		(riskConfig.assetCount as number) > 0 ? riskConfig.assetCount : 0;
	const assetFilterName = assetFilters?.find(
		(f: AssetFilterListItem) => f._id === riskConfig.assetFilterId,
	)?.name;

	return (
		<div
			key={riskConfig.configId}
			style={{
				fontSize: '1rem',
			}}>
			<PropertyCard
				columns={2}
				forceMenu
				className="adjusted-menu-position"
				menuItems={menuItems()}
				properties={[
					{
						title: t('Last Run'),
						value: lastRun.length == 0 ? '-' : lastRun,
						ellipsis: true,
					},
					{
						title: t('Status'),
						type: riskConfig.status ? 'status' : 'text',
						value: getRiskStatus(
							riskConfig.status as RiskCalcStatusResult,
						),
					},
					{
						title: t('Run By'),
						value: riskConfig.runBy
							? formatRunBy(riskConfig.runBy)
							: '-',
						ellipsis: true,
					},
					{
						title:
							riskConfig.riskMethod == 'added'
								? t('Formula (LoF + CoF)')
								: t('Formula (LoF x CoF)'),
						value: getFormulaValue(riskConfig),
						ellipsis: true,
					},
					{
						title: t('System and Asset Type'),
						value: typeCell(riskConfig.assetType),
					},
					isSaveFilterOn
						? {
								title: t('Result Count'),
								value: riskConfig.assetFilterId ? (
									<FilteredResultCount
										title={`${t('Filtered risk model')} (${
											assetFilterName || 'partial results'
										})`}>
										{resultCount}
										<FilterIcon color="secondary" />
									</FilteredResultCount>
								) : (
									<div>{resultCount}</div>
								),
						  }
						: {
								title: t('Asset Count'),
								value:
									isAssetCountsReady && assetCounts
										? assetCounts.find(
												(assetCount: AssetCount) => {
													return (
														assetCount.systemType ==
															riskConfig.systemType &&
														assetCount.assetType ==
															riskConfig.assetType
													);
												},
										  )?.count ?? '-'
										: '-',
						  },
				]}
				title={renderHeader(
					riskConfig.name ?? t('Default Risk Model'),
					riskConfig.publishState,
				)}
			/>
		</div>
	);
};
export default RiskListCard;
